import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Checkbox } from '@/components/ui/checkbox'
import { BUSINESS_SECTORS_WITH_SUBS } from '@/utils/businessSectors'
import { MapPin, DollarSign, Clock, Briefcase, X, ChevronDown, ChevronRight } from 'lucide-react'

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

const BUDGET_RANGES = [
  'Under $100K',
  '$100K – $250K',
  '$250K – $500K',
  '$500K – $1M',
  '$1M – $2M',
  '$2M+',
]

const TIMEFRAMES = [
  'Ready now',
  'Within 3 months',
  '3–6 months',
  '6–12 months',
  'Just exploring',
]

interface Step1Data {
  locations: string[]
  budget: string
  timeframe: string
  businessTypes: string[]
}

interface Props {
  data: Step1Data
  onChange: (data: Step1Data) => void
  onNext: () => void
}

export default function BuyerIntakeStep1({ data, onChange, onNext }: Props) {
  const [sectorSearch, setSectorSearch] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])

  const toggleLocation = (loc: string) => {
    const next = data.locations.includes(loc)
      ? data.locations.filter(l => l !== loc)
      : [...data.locations, loc]
    onChange({ ...data, locations: next })
  }

  const toggleSubCategory = (sub: string) => {
    const next = data.businessTypes.includes(sub)
      ? data.businessTypes.filter(s => s !== sub)
      : [...data.businessTypes, sub]
    onChange({ ...data, businessTypes: next })
  }

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    )
  }

  const toggleAllSubsInCategory = (subs: string[]) => {
    const allSelected = subs.every(s => data.businessTypes.includes(s))
    if (allSelected) {
      onChange({ ...data, businessTypes: data.businessTypes.filter(s => !subs.includes(s)) })
    } else {
      const newTypes = [...new Set([...data.businessTypes, ...subs])]
      onChange({ ...data, businessTypes: newTypes })
    }
  }

  const isValid = data.locations.length > 0 && data.budget && data.timeframe && data.businessTypes.length > 0

  const filteredSectors = sectorSearch
    ? BUSINESS_SECTORS_WITH_SUBS
        .map(sector => ({
          ...sector,
          subCategories: sector.subCategories.filter(s =>
            s.toLowerCase().includes(sectorSearch.toLowerCase()) ||
            sector.name.toLowerCase().includes(sectorSearch.toLowerCase())
          ),
        }))
        .filter(sector => sector.subCategories.length > 0)
    : BUSINESS_SECTORS_WITH_SUBS

  return (
    <div className="space-y-8">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-muted-foreground font-medium">
          <span>Matching Preferences</span>
          <span>Smart Matching</span>
        </div>
        <Progress value={100} className="h-2" />
      </div>

      {/* Hero positioning */}
      <div className="text-center space-y-3">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground leading-tight">
          Tell Us What You're Looking For —{' '}
          <span className="text-primary">We'll Handle the Rest.</span>
        </h1>
        <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
          Your input shapes your deal flow. The more specific you are, the better we can match you with businesses that actually fit.
        </p>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {/* Location */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <MapPin className="h-4 w-4 text-primary" />
            Preferred Location
          </Label>
          <p className="text-xs text-muted-foreground -mt-1">Select all states you're interested in.</p>
          <div className="flex flex-wrap gap-2">
            {AUSTRALIAN_STATES.map(state => (
              <button
                key={state}
                type="button"
                onClick={() => toggleLocation(state)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                  data.locations.includes(state)
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-background text-foreground border-border hover:border-primary/40'
                }`}
              >
                {state}
              </button>
            ))}
          </div>
        </div>

        {/* Budget */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <DollarSign className="h-4 w-4 text-primary" />
            Budget Range
          </Label>
          <Select value={data.budget} onValueChange={v => onChange({ ...data, budget: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select your budget range" />
            </SelectTrigger>
            <SelectContent>
              {BUDGET_RANGES.map(b => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Timeframe */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4 text-primary" />
            When are you looking to purchase?
          </Label>
          <Select value={data.timeframe} onValueChange={v => onChange({ ...data, timeframe: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select your timeframe" />
            </SelectTrigger>
            <SelectContent>
              {TIMEFRAMES.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Business Type with Sub-categories */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <Briefcase className="h-4 w-4 text-primary" />
            Business Type
          </Label>
          <p className="text-xs text-muted-foreground -mt-1">Select all categories and sub-categories that interest you.</p>

          {data.businessTypes.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.businessTypes.map(s => (
                <Badge key={s} variant="secondary" className="gap-1 cursor-pointer" onClick={() => toggleSubCategory(s)}>
                  {s} <X className="h-3 w-3" />
                </Badge>
              ))}
            </div>
          )}

          <input
            type="text"
            placeholder="Search business types..."
            value={sectorSearch}
            onChange={e => setSectorSearch(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />

          <div className="max-h-56 overflow-y-auto rounded-md border border-border p-2 space-y-1">
            {filteredSectors.map(sector => {
              const isExpanded = expandedCategories.includes(sector.name) || !!sectorSearch
              const selectedCount = sector.subCategories.filter(s => data.businessTypes.includes(s)).length
              const allSelected = selectedCount === sector.subCategories.length

              return (
                <div key={sector.name}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory(sector.name)}
                      className="flex items-center gap-1.5 flex-1 text-left px-2 py-1.5 rounded text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      {sector.name}
                      {selectedCount > 0 && (
                        <span className="text-xs text-primary font-normal ml-1">({selectedCount})</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAllSubsInCategory(sector.subCategories)}
                      className="text-xs text-primary hover:underline px-2 py-1 shrink-0"
                    >
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="ml-6 space-y-0.5 pb-1">
                      {sector.subCategories.map(sub => (
                        <label
                          key={sub}
                          className="flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer hover:bg-muted transition-colors"
                        >
                          <Checkbox
                            checked={data.businessTypes.includes(sub)}
                            onCheckedChange={() => toggleSubCategory(sub)}
                          />
                          <span className={data.businessTypes.includes(sub) ? 'text-primary font-medium' : 'text-foreground'}>
                            {sub}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="space-y-2 pt-2">
        <Button
          onClick={onNext}
          disabled={!isValid}
          size="lg"
          className="w-full"
          variant="hero"
        >
          Save My Preferences
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Helps us filter irrelevant listings and send you better matches.
        </p>
      </div>
    </div>
  )
}
